/**
 * Live camera distance + PLATEAU street-detail zoom gate (stable mount policy).
 * Distance is orbit-target range in scene metres (~ground metres for Tokyo 3857).
 */
import type { CSSProperties } from 'react';
import { tokens } from '@/design';
import {
  PLATEAU_STREET_DETAIL_DISTANCE_M,
  PLATEAU_STREET_DETAIL_RELEASE_DISTANCE_M,
  formatPlateauCameraDistanceM,
} from '@/engines/giro3d/plateauMountPolicy';

export interface PlateauZoomGateChipProps {
  readonly distanceM: number | null;
  readonly streetDetailActive: boolean;
  /** When false the chip is hidden (stable mount policy off). */
  readonly stableMountEnabled: boolean;
  /** Visible detail layers not yet mounted (0 when all allowed layers are on). */
  readonly deferredCount: number;
  /** Detail layers currently mounted (roads, furniture, veg…). */
  readonly mountedDetailCount: number;
  /** Per-layer tile progress snapshot. Shown only when streetDetailActive. */
  readonly layerTileProgress?: Map<string, { loaded: number; total: number; inFlight: number }>;
}

/** Map raw layer ID fragments to short display names. */
function layerDisplayName(layerId: string): string | null {
  const id = layerId.toLowerCase();
  if (id.includes('tran')) return 'Roads';
  if (id.includes('frn')) return 'Furn';
  if (id.includes('veg')) return 'Veg';
  if (id.includes('brid')) return 'Brid';
  return null;
}

/** Build "Roads: 42 | Furn: 0 | Veg: 3" string from per-layer progress map. */
function buildLayerCountLine(
  progress: Map<string, { loaded: number; total: number; inFlight: number }>,
): string | null {
  const parts: string[] = [];
  for (const [layerId, stats] of progress) {
    const name = layerDisplayName(layerId);
    if (name) parts.push(`${name}: ${stats.loaded}`);
  }
  return parts.length > 0 ? parts.join(' | ') : null;
}

export function PlateauZoomGateChip({
  distanceM,
  streetDetailActive,
  stableMountEnabled,
  deferredCount,
  mountedDetailCount,
  layerTileProgress,
}: PlateauZoomGateChipProps) {
  if (!stableMountEnabled) return null;

  const distLabel = formatPlateauCameraDistanceM(distanceM);
  const withinEnter =
    distanceM != null && distanceM <= PLATEAU_STREET_DETAIL_DISTANCE_M;
  const withinHold =
    distanceM != null && distanceM <= PLATEAU_STREET_DETAIL_RELEASE_DISTANCE_M;

  let statusLine: string;
  if (streetDetailActive) {
    if (mountedDetailCount > 0 && deferredCount === 0) {
      statusLine = `Street detail ON — ${mountedDetailCount} detail layer${mountedDetailCount === 1 ? '' : 's'} mounted`;
    } else if (deferredCount > 0) {
      statusLine = `Street detail ON — ${deferredCount} layer${deferredCount === 1 ? '' : 's'} still waiting`;
    } else {
      statusLine = 'Street detail ON — toggle roads/furniture in layer panel';
    }
  } else if (withinEnter) {
    statusLine = `Within ${PLATEAU_STREET_DETAIL_DISTANCE_M} m — detail layers unlocking…`;
  } else if (withinHold) {
    statusLine = `Zoom closer (≤ ${PLATEAU_STREET_DETAIL_DISTANCE_M} m) for roads, furniture, trees`;
  } else {
    statusLine = `Zoom to ≤ ${PLATEAU_STREET_DETAIL_DISTANCE_M} m for roads, furniture, vegetation`;
  }

  const layerCountLine =
    streetDetailActive && layerTileProgress
      ? buildLayerCountLine(layerTileProgress)
      : null;

  return (
    <div
      role="status"
      aria-live="polite"
      data-testid="plateau-zoom-gate-chip"
      data-street-detail={streetDetailActive ? 'active' : 'deferred'}
      style={chipStyle(streetDetailActive)}
      title="Camera distance is measured to the orbit target (3D scene metres), not height above ground."
    >
      <span style={distanceStyle}>Camera: {distLabel}</span>
      <span style={statusStyle}>{statusLine}</span>
      {layerCountLine ? (
        <span data-testid="plateau-layer-tile-counts" style={layerCountStyle}>
          {layerCountLine}
        </span>
      ) : null}
      {streetDetailActive ? (
        <span style={hintStyle}>
          Tilt to street angle — top-down view hides roads & furniture under roofs
        </span>
      ) : null}
    </div>
  );
}

function chipStyle(active: boolean): CSSProperties {
  return {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-end',
    gap: 2,
    marginTop: tokens.space[1],
    padding: `${tokens.space[1]}px ${tokens.space[2]}px`,
    borderRadius: tokens.radius.pill,
    background: tokens.color.surface[3],
    color: tokens.color.ink.primary,
    border: `${tokens.border.width.thin}px solid ${
      active
        ? tokens.color.semantic.success
        : tokens.color.semantic.info
    }`,
    fontSize: 12,
    lineHeight: 1.35,
    maxWidth: 300,
    textAlign: 'right',
  };
}

const distanceStyle: CSSProperties = {
  fontWeight: 600,
  whiteSpace: 'nowrap',
};

const statusStyle: CSSProperties = {
  opacity: 0.9,
  whiteSpace: 'normal',
};

const hintStyle: CSSProperties = {
  opacity: 0.75,
  fontSize: 11,
  fontStyle: 'italic',
  whiteSpace: 'normal',
};

const layerCountStyle: CSSProperties = {
  opacity: 0.85,
  fontSize: 11,
  fontFamily: tokens.font.family.mono,
  whiteSpace: 'nowrap',
};
